import { useEffect, useRef, useState } from "react";
import { ws } from "@/lib/websocket-wrapper";

const DEFAULT_GRACE_PERIOD_MS = 30_000;
const COUNTDOWN_THRESHOLD_MS = 60_000;

export function useWsConnected(gracePeriodMs = DEFAULT_GRACE_PERIOD_MS): {
	isConnected: boolean;
	reconnectCountdown: number | null;
	connectionEpoch: number;
} {
	const [isConnected, setIsConnected] = useState(ws.isConnected);
	const [reconnectCountdown, setReconnectCountdown] = useState<number | null>(
		null
	);
	// TECH-3051: bumps on EVERY socket "open", reconnects included, even the ones
	// the grace period hides from `isConnected`, where a close and open inside the
	// grace window never flips the boolean. Consumers depend on this to re-run the
	// connect chain (workspace.setCurrent, then channel.list, then users.list and
	// the rest) on every reconnect rather than only the first connect. Without it
	// a reconnect skips the reload and the server connection stays
	// workspace-less.
	const [connectionEpoch, setConnectionEpoch] = useState(0);
	const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
		null
	);

	useEffect(() => {
		const clearCountdown = () => {
			setReconnectCountdown(null);
			if (countdownIntervalRef.current) {
				clearInterval(countdownIntervalRef.current);
				countdownIntervalRef.current = null;
			}
		};

		const openHandler = () => {
			if (graceTimerRef.current) {
				clearTimeout(graceTimerRef.current);
				graceTimerRef.current = null;
			}
			clearCountdown();
			setIsConnected(true);
			// Always advance, even when isConnected was already true after a fast
			// reconnect inside the grace window. This is the reconnect signal.
			setConnectionEpoch((epoch) => epoch + 1);
		};

		const closeHandler = () => {
			// Start the grace period, so the disconnected state only shows after it.
			// A connection that keeps failing closes once per attempt, and the grace
			// runs from the first of them. A second timer would outlive the open
			// that cancels this one, and mark an open socket disconnected.
			if (graceTimerRef.current) return;
			graceTimerRef.current = setTimeout(() => {
				graceTimerRef.current = null;
				setIsConnected(false);
			}, gracePeriodMs);
		};

		const reconnectingHandler = (event: Event) => {
			const { delay } = (event as CustomEvent<{ delay: number }>).detail;

			if (delay < COUNTDOWN_THRESHOLD_MS) {
				clearCountdown();
				return;
			}

			let remaining = Math.ceil(delay / 1000);
			setReconnectCountdown(remaining);

			clearCountdown();
			countdownIntervalRef.current = setInterval(() => {
				remaining -= 1;
				if (remaining <= 0) {
					clearCountdown();
				} else {
					setReconnectCountdown(remaining);
				}
			}, 1000);
		};

		ws.addEventListener("open", openHandler);
		ws.addEventListener("close", closeHandler);
		ws.addEventListener("reconnecting", reconnectingHandler);

		return () => {
			ws.removeEventListener("open", openHandler);
			ws.removeEventListener("close", closeHandler);
			ws.removeEventListener("reconnecting", reconnectingHandler);
			if (graceTimerRef.current) {
				clearTimeout(graceTimerRef.current);
				// Cleared, so no longer running: the close handler would otherwise
				// take it for a live one and never start another.
				graceTimerRef.current = null;
			}
			clearCountdown();
		};
	}, [gracePeriodMs]);

	return { isConnected, reconnectCountdown, connectionEpoch };
}
